import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { MobileMediaField } from '../../backbone/shared/models/MobileMediaField';
import { analyzeImageQuality } from '../../backbone/features/mobileForm/imageField/imageQualityChecker';
import { resizeImage } from '../../shared/utils/files';
import { BaseZenputModel } from '../shared/forms';
import { createUUID } from '../utils/files';
import { ZenputFiles, ZenputFile } from '../../backbone/shared/models/files';
import { IMAGE_MAX_SIZE, SUBMISSION_SOURCE } from '../../constants';

export const FollowUpAction = BaseZenputModel.extend({
    defaults: {
        assigned_user: null,
        completed_user: null,
        created_user: null,
        data: null,
        date_assigned: null,
        date_completed: null,
        date_created: null,
        date_deleted: null,
        date_due: null,
        deleted_user: null,
        modified_user: null,
        has_delete_permission: null,
        has_update_permission: null,
        id: null,
        location: null,
        role: null,
        source_id: null,
        source_title: null,
        source_type: null,
        reply_type: null,
        status: null,
        team: null,
        title: null
    },

    delete: function (onSuccess, onError) {
        this.save(
            {},
            {
                use_id_param: true,
                type: 'DELETE',
                success: () => {
                    onSuccess();
                },
                error: () => {
                    onError();
                }
            }
        );
    },

    patch: function (fieldsToPatch, onComplete, onError) {
        this.save(fieldsToPatch, {
            patch: true,
            use_id_param: true,
            type: 'PATCH',
            success: (response) => {
                onComplete(response.attributes);
            },
            error: () => {
                onError();
            }
        });
    },

    cleanFollowUpActionsForApi: function (actions) {
        return actions.map(({ images, location: { time_zone, ...otherLocationProps }, ...action }) => ({
            ...action,
            images: images?.map((image) => image.s3_key) ?? [],
            location: { ...otherLocationProps }
        }));
    },

    formatFollowUpActionsForBulkCreate: function (actions, sourceType, sourceId) {
        const source = sourceType === SUBMISSION_SOURCE ? { submission_id: sourceId } : { task_id: sourceId };

        return actions.map((action) => ({
            title: action.title,
            date_due: action.due_date,
            location: action.location,
            source_type: sourceType,
            reply_type: action.require_photo ? 'photo' : 'basic',
            assigned_user: action.assigned_user ?? null,
            data: {
                description: action.description ?? '',
                images: action.images ?? [],
                reply: {},
                source: source
            }
        }));
    },

    createMultipleFollowUpActions: function (followUpActions, sourceType, sourceId, onSuccess, onError) {
        const cleanedForApi = this.cleanFollowUpActionsForApi(followUpActions);
        const cleanedForBulkCreate = this.formatFollowUpActionsForBulkCreate(cleanedForApi, sourceType, sourceId);

        $f.mobile.ajax({
            url: '/api/v3/follow_up_actions/bulk-create/',
            dataType: 'json',
            type: 'POST',
            cache: false,
            contentType: 'application/json',
            data: JSON.stringify(cleanedForBulkCreate),
            success: function (response) {
                onSuccess(response);
            },
            error: function (response) {
                onError(response);
            }
        });
    },

    url: function () {
        return `/api/v3/follow_up_actions/${this.get('id')}/`;
    }
});

export const FollowUpActionField = MobileMediaField.extend({
    default: {
        id: null,
        parent_upload_guid: null
    },

    initialize: function (attrs, options) {
        options = options || {};
        $.extend(this, options);

        FollowUpActionField.__super__.initialize.call(this, attrs, options);
    },

    create_field_upload_filename: function (file_entry) {
        return $f.storage.create_s3_filepath($f.active_company.id, 'follow_up_actions', null, file_entry.name);
    },

    get_parent_upload_guid: function () {
        return this.parent_upload_guid;
    }
});

export const uploadFollowUpActionsFiles = async (files, parent_upload_guid) => {
    const field = new FollowUpActionField({ type: 'image' }, { parent_upload_guid });

    const originalFiles = files.map((file) => file);

    await new Promise((resolve) => {
        field.save_files({ add: files }, () => {
            resolve();
        });
    });

    // save_files mutates `files` in-place removing failed uploads (same instances, so cid is stable).
    // field.get('file') creates new instances via toJSON(), so we match by order not cid.
    const uploadedFileIdsSet = new Set(files.map((file) => file.cid));
    const uploadedFiles = field.get('file');
    const uploadedFilesPaths = uploadedFiles ? uploadedFiles.map((file) => file.get('path')) : [];
    let successIdx = 0;
    return originalFiles.map((file) => (uploadedFileIdsSet.has(file.cid) ? uploadedFilesPaths[successIdx++] : null));
};

export const collectCameraNative = async () => {
    const photo = await Camera.getPhoto({
        quality: 90,
        resultType: CameraResultType.Uri,
        width: IMAGE_MAX_SIZE,
        height: IMAGE_MAX_SIZE,
        source: CameraSource.Camera
    });

    return photo;
};

/**
 * Allows the user to upload images from os native file picker window
 * @param {boolean} photoQualityAnalysis - if true, it'll analyze the image quality
 * @returns
 */
export const collectMediaWeb = async (photoQualityAnalysis) => {
    return new Promise((resolve) => {
        $f.mobile.uploads.web_filestore.pick_new_file(
            {
                allowed_file_types: 'image/*',
                allow_multiple: true,
                is_media: true
            },
            async function (files_list) {
                const displayUrls = await Promise.all(
                    files_list.map(async (file) => {
                        const rawFile = file.get('raw_file');
                        const extension = rawFile.name.match(/(\.\w+$)/)?.[0];
                        const renamedFile = new File([rawFile], `${createUUID()}${extension || '.jpg'}`, {
                            type: rawFile.type
                        });
                        const resizedFile = await resizeImage(renamedFile, 2048);
                        file.set('raw_file', resizedFile);

                        const filePath = URL.createObjectURL(rawFile);

                        let imageQualityAnalysisResult;

                        if (photoQualityAnalysis) {
                            imageQualityAnalysisResult = await analyzeImageQuality(filePath);
                            file.set('laplacian_variance', imageQualityAnalysisResult.laplacianVariance);
                            file.set('is_low_quality', imageQualityAnalysisResult.isBad);
                        }

                        return {
                            name: rawFile.name,
                            raw_file: filePath,
                            size: resizedFile.size,
                            type: rawFile.type,
                            is_low_quality: imageQualityAnalysisResult?.isBad ?? null,
                            laplacian_variance: imageQualityAnalysisResult?.laplacianVariance ?? null,
                            s3_key: null
                        };
                    })
                );

                resolve({ files: files_list, displayUrls });
            }
        );
    });
};

export const collectMediaNative = async () => {
    const files = await Camera.pickImages({
        quality: 90,
        width: IMAGE_MAX_SIZE,
        height: IMAGE_MAX_SIZE
    });

    if (!files || !files.photos.length) {
        return [];
    }

    return files.photos;
};

/**
 * Converts a Photo | Gallery object into a Zenput File object
 * @param {import('@capacitor/camera').Photo | import('@capacitor/camera').GalleryPhoto} photo
 * @param {boolean} photoQualityAnalysis - if true, it'll analyze the image quality
 * @returns {Promise<import('../../backbone/shared/models/files').ZenputFile>}
 */
export const getZenputFileFromPhoto = async (photo, photoQualityAnalysis) => {
    let { path, webPath } = photo;

    if (!path) {
        return null;
    }

    if (typeof path === 'string' && path.indexOf('://') === -1) {
        path = 'file://' + path;
    }
    let imageQualityAnalysisResult;

    if (photoQualityAnalysis) {
        imageQualityAnalysisResult = await analyzeImageQuality(webPath);
    }

    const newFile = new ZenputFile({
        ...ZenputFile.defaults,
        id: createUUID(),
        mimetype: 'image/jpg',
        name: photo.path ? photo.path.split('/').pop() : `temp.${photo.format}`,
        is_low_quality: imageQualityAnalysisResult?.isBad,
        laplacian_variance: imageQualityAnalysisResult?.laplacianVariance,
        type: photo.format,
        ...(typeof path === 'string' ? { url_native: path } : { raw_file: path })
    });

    return newFile;
};

/**
 * Converts Photo | Gallery objects list into a Zenput File objects list
 * @param {(import('@capacitor/camera').Photo | import('@capacitor/camera').GalleryPhoto)[]} photos
 * @param {boolean} photoQualityAnalysis - if true, it'll analyze the image quality
 * @returns {Promise<import('../../backbone/shared/models/files').ZenputFiles>}
 */
export const getZenputFilesFromPhotos = async (photos, photoQualityAnalysis) => {
    const zenputFiles = await Promise.all(photos.map((photo) => getZenputFileFromPhoto(photo, photoQualityAnalysis)));

    return new ZenputFiles(zenputFiles);
};
